import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import moment from 'moment';
import { useState } from 'react';
import { Button } from '../../../../web/components-v2/Button';
import { Checkbox } from '../../../../web/components-v2/Checkbox';
import {
	Dialog,
	DialogBody,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogPopup,
	DialogTitle,
} from '../../../../web/components-v2/Dialog';
import { getRegistrantEmail, getRegistrantName } from './coverageState';
import { coverageDialogStyles } from './styles';

interface EventAutomationCoverageDialogProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	coverage: Api.IEventAutomationCoverage;
	eventName?: string;
	isEventPast: boolean;
	isRetargeted: boolean;
	reportUrl?: string;
	/** When set, registrants can be selected and started; omitted wherever starting isn't offered. */
	startableContactIds?: string[];
	onStart?: (contactIds: string[]) => void;
}

export function EventAutomationCoverageDialog({
	open,
	onOpenChange,
	...contentProps
}: EventAutomationCoverageDialogProps) {
	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogPopup styles={[coverageDialogStyles.popup]}>
				<CoverageDialogContent {...contentProps} />
			</DialogPopup>
		</Dialog>
	);
}

function CoverageDialogContent({
	coverage,
	eventName,
	isEventPast,
	isRetargeted,
	reportUrl,
	startableContactIds,
	onStart,
}: Omit<EventAutomationCoverageDialogProps, 'open' | 'onOpenChange'>) {
	const [excludedContactIds, setExcludedContactIds] = useState<Set<string>>(() => new Set());
	const missed = coverage.unenrolledCount;
	const notShownCount = missed - coverage.registrants.length;
	const canSelect = !!startableContactIds && !!onStart;
	const selectedContactIds = (startableContactIds ?? []).filter(id => !excludedContactIds.has(id));
	const allSelected = selectedContactIds.length === (startableContactIds?.length ?? 0);

	const toggle = (contactId: string, checked: boolean) =>
		setExcludedContactIds(previous => {
			const next = new Set(previous);
			if (checked) {
				next.delete(contactId);
			} else {
				next.add(contactId);
			}
			return next;
		});
	const toggleAll = (checked: boolean) => setExcludedContactIds(checked ? new Set() : new Set(startableContactIds));

	return (
		<>
			<DialogHeader>
				<DialogTitle>
					{missed === 1 ? '1 registrant is not enrolled' : `${missed} registrants are not enrolled`}
				</DialogTitle>
				<DialogDescription>
					{isRetargeted
						? `This automation’s trigger points at a different event, so registrations for ${eventName || 'this event'} are not enrolled.`
						: `They registered for ${eventName || 'this event'} before this automation was set up, so it ${
								isEventPast ? 'never started' : 'won’t start'
							} for them.`}
				</DialogDescription>
			</DialogHeader>
			<DialogBody>
				<div className={css(coverageDialogStyles.columnHeaders)}>
					<span className={css(coverageDialogStyles.selectable)}>
						{canSelect ? (
							<Checkbox
								size='lg'
								aria-label='Select all registrants'
								checked={allSelected ? true : selectedContactIds.length > 0 ? 'indeterminate' : false}
								onCheckedChange={checked => toggleAll(checked === true)}
							/>
						) : null}
						Registrant
					</span>
					<span>Registered</span>
				</div>
				<ul className={css(coverageDialogStyles.list)}>
					{coverage.registrants.map(registrant => {
						const contactId = registrant.contact?.id;
						const name = getRegistrantName(registrant);
						return (
							<li key={registrant.id} className={css(coverageDialogStyles.row)}>
								<div className={css(coverageDialogStyles.selectable, coverageDialogStyles.registrantCell)}>
									{canSelect ? (
										contactId ? (
											<Checkbox
												size='lg'
												aria-label={`Include ${name}`}
												checked={!excludedContactIds.has(contactId)}
												onCheckedChange={checked => toggle(contactId, checked === true)}
											/>
										) : (
											<span className={css(coverageDialogStyles.checkboxPlaceholder)} />
										)
									) : null}
									<div className={css(coverageDialogStyles.registrant)}>
										<span className={css(coverageDialogStyles.name)}>{name}</span>
										<span className={css(coverageDialogStyles.secondary)}>{getRegistrantEmail(registrant)}</span>
									</div>
								</div>
								<span className={css(coverageDialogStyles.secondary)}>
									{registrant.creationDate ? moment(registrant.creationDate).format('MMM D, YYYY') : ''}
								</span>
							</li>
						);
					})}
				</ul>
				{notShownCount > 0 ? (
					<p
						className={css(coverageDialogStyles.secondary, coverageDialogStyles.note)}
					>{`…and ${notShownCount} more not shown.`}</p>
				) : null}
				<p className={css(coverageDialogStyles.footnote)}>Registrations without a contact record are not counted.</p>
			</DialogBody>
			{reportUrl || canSelect ? (
				<DialogFooter>
					{reportUrl ? (
						<Button asChild variant='outline'>
							<a href={reportUrl} target='_blank' rel='noreferrer'>
								Open the event report
							</a>
						</Button>
					) : null}
					{canSelect ? (
						<Button disabled={selectedContactIds.length === 0} onClick={() => onStart(selectedContactIds)}>
							{selectedContactIds.length === 1 ? 'Start for 1 person' : `Start for ${selectedContactIds.length} people`}
						</Button>
					) : null}
				</DialogFooter>
			) : null}
		</>
	);
}
